import {
  getSalesOrderControllerGetSalesOrderNotesQueryKey,
  useSalesOrderControllerUpdateSalesOrderNote,
} from '@/generated/api/sales-order.ts';
import { Tabs } from '@mantine/core';
import type { GetSalesOrderNotesDto } from '@/generated/api/pacApi.schemas.ts';
import useSidebarStack from '@/common/sidebar/useSidebarStack.ts';
import useSalesOrderNotes from '@/features/finance/sales-orders/hooks/useSalesOrderNotes.tsx';
import useCreateSalesOrderNote from '@/features/finance/sales-orders/hooks/useCreateSalesOrderNote.tsx';
import { openSalesOrderNoteAttachment } from '@/features/finance/sales-orders/openSalesOrderNoteAttachment.ts';
import { useMemo } from 'react';
import { NotesPanel } from '@/common/components/artifacts/NotePanel.tsx';

export default function SalesOrderNotesTabBody({
  salesOrderId,
}: {
  salesOrderId: string;
}) {
  const sidebarStack = useSidebarStack();

  const notesQueryKey = getSalesOrderControllerGetSalesOrderNotesQueryKey(
    salesOrderId ?? '',
  );
  const notesQuery = useSalesOrderNotes(salesOrderId);

  const createSalesOrderNote = useCreateSalesOrderNote();

  const updateSalesOrderNote = useSalesOrderControllerUpdateSalesOrderNote({
    mutation: {
      onMutate: async (newNote, context) => {
        await context.client.cancelQueries({ queryKey: notesQueryKey });

        const previousNotes: GetSalesOrderNotesDto | undefined =
          context.client.getQueryData(notesQueryKey);

        context.client.setQueryData(
          notesQueryKey,
          (old: GetSalesOrderNotesDto) => {
            return {
              ...old,
              notes: old.notes.map((note) => {
                if (note.salesOrderNoteId === newNote.salesOrderNoteId) {
                  return {
                    ...note,
                    message: newNote.data.message,
                  };
                } else {
                  return note;
                }
              }),
            };
          },
        );

        return previousNotes;
      },
      onError: (_err, _newNote, onMutateResult, context) => {
        context.client.setQueryData(notesQueryKey, onMutateResult);
      },
      onSettled: (_data, _error, _variables, _onMutateResult, context) =>
        context.client.invalidateQueries({ queryKey: notesQueryKey }),
    },
  });

  const notes = useMemo(
    () =>
      (notesQuery.data?.notes ?? []).map((note) => ({
        id: note.salesOrderNoteId,
        author: note.author,
        message: note.message,
        timestamp: note.timestamp,
        actions: note.actions,
        attachments: note.attachments ?? [],
      })),
    [notesQuery.data],
  );

  return (
    <Tabs.Panel
      value="notes"
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <NotesPanel
        showHeader={false}
        notes={notes}
        canUpload={notesQuery.data?.actions?.includes('upload') ?? false}
        loading={notesQuery.isLoading || createSalesOrderNote.isPending}
        createNote={async (message, files) => {
          await createSalesOrderNote.mutateAsync({
            salesOrderId: salesOrderId,
            data: {
              message,
              files,
            },
          });
        }}
        updateNote={(noteId, message) =>
          updateSalesOrderNote.mutate({
            salesOrderId: salesOrderId,
            salesOrderNoteId: noteId,
            data: { message },
          })
        }
        openAttachment={(noteId, attachment) =>
          void openSalesOrderNoteAttachment(salesOrderId, noteId, attachment)
        }
        onClose={() => sidebarStack.popItem()}
      />
    </Tabs.Panel>
  );
}
